import { useState } from 'react';
import { createPortal } from 'react-dom';
import { GLASS_ACCENT, glassSurface } from './glass';
import { lottie } from '../../assets/figma/lottie';
import { LottieIcon } from './LottieIcon';
import { useUiStore } from '../../store/ui';

export type Tab = 'home' | 'cards' | 'wallet' | 'stats' | 'profile';

type Props = {
  active: Tab;
  onChange: (t: Tab) => void;
  /** Hide while a full-screen push covers the tabs (send/swap/coin/…). */
  obscured?: boolean;
};

// Icon tints — baked into Lottie JSON (not CSS filter). Safari/WKWebView
// shifts sepia/hue-rotate chains toward purple; real RGB stays blue.
const GREY = '#A8A8A8';
const WHITE = '#FFFFFF';
const BLUE = '#377DFF';
const TAB_CENTER: Record<Tab, string> = {
  home: '34px',
  cards: '102px',
  wallet: '50%',
  stats: 'calc(100% - 102px)',
  profile: 'calc(100% - 34px)',
};

function SideTab({
  tab,
  anim,
  active,
  playKey,
  onClick,
}: {
  tab: Tab;
  anim: unknown;
  active: boolean;
  playKey: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={tab}
      aria-current={active ? 'page' : undefined}
      className="relative flex items-center justify-center overflow-hidden rounded-full transition-transform duration-300 active:scale-95"
      style={{ width: 68, height: 64 }}
    >
      <div
        className="relative z-10"
        style={{
          // Lens magnify: the active icon scales up inside the selection capsule
          // with a springy overshoot. The grow is delayed so it lands as the
          // sliding capsule arrives (esp. on long jumps); the leaving icon
          // shrinks at once.
          transform: active ? 'scale(1.16)' : 'scale(1)',
          transition: 'transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          transitionDelay: active ? '150ms' : '0ms',
        }}
      >
        <LottieIcon
          animationData={anim}
          size={24}
          color={active ? BLUE : GREY}
          playToken={active ? playKey : 0}
        />
      </div>
    </button>
  );
}

export function BottomNav({ active, onChange, obscured = false }: Props) {
  const [playKey, setPlayKey] = useState(0);
  const sheetHidden = useUiStore((s) => s.sheetCount > 0);
  const hidden = sheetHidden || obscured;
  const walletActive = active === 'wallet';
  // Only Telegram needs this pad. On the home-screen shortcut it lifts the
  // pill and fills the gap with --app-canvas gray. Safari's toolbar already
  // clears the home indicator the same way.
  const clearHomeIndicator = !!window.Telegram?.WebApp?.initData;

  const select = (tab: Tab) => {
    onChange(tab);
    setPlayKey((k) => k + 1);
  };

  // Portaled to document.body (same as ScreenBackdrop top bleed): inside #root
  // iOS Safari clips fixed paint to the safe-area and leaves a solid grey
  // rectangle behind the home-indicator swipe. Visuals unchanged.
  // Hide by sliding `bottom` (+ opacity), never `transform` — backdrop-filter
  // only samples the page when no ancestor is transformed.
  const node = (
    <div
      data-glass-ignore
      className="pointer-events-none fixed inset-x-0 z-30 flex justify-center transition-[bottom,opacity] duration-500 ease-out"
      style={{
        // Padding (not offset) clears the home-indicator / Android floor so
        // nothing paints a gray band under the pill. --nav-bottom-pad floors
        // at 12px when Telegram reports 0 inset (common on Android).
        bottom: hidden ? '-160px' : 0,
        paddingBottom: clearHomeIndicator ? 'var(--nav-bottom-pad)' : 0,
        opacity: hidden ? 0 : 1,
      }}
    >
      <div
        className="pointer-events-auto relative overflow-visible"
        style={{
          width: 'min(370px, calc(100vw - 24px))',
          height: 80,
        }}
      >
        {/* Clip lives on the wrapper. backdrop-filter on the same element is
            not clipped by WebKit and paints a rectangle under the pill — visible
            on the cards / staking holo, faint on the flat profile surface. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 right-0 overflow-hidden rounded-full"
          style={{ top: 6, height: 68, borderRadius: 999 }}
        >
          <div
            className="h-full w-full"
            style={{
              // Denser than the buttons: the bar floats over the plain page
              // backdrop, where a thin pane would barely separate from it.
              ...glassSurface('thick'),
              // Rim only — outer drop-shadow pools under the floating bar.
              boxShadow:
                'inset 0 0 0 1px rgba(255,255,255,0.55), inset 0 1px 0 0 rgba(255,255,255,0.9), inset 0 -1px 0 0 rgba(255,255,255,0.3)',
            }}
          />
        </div>

        {/* Selection capsule — sibling of the bar so the bloom can bleed past
            the pill edges. Sits above the glass, under the tab icons.
            Geometry mirrors the outer pill: bar is 68× fully-round (r=34),
            inset ~7px → inner r ≈ 27, so the curves stay parallel. */}
        <span
          aria-hidden
          className="pointer-events-none absolute z-[5] transition-[left,opacity] duration-[480ms] ease-out"
          style={{
            left: TAB_CENTER[active],
            // Vertically centres on the 68px bar that sits at top: 6.
            top: 40,
            opacity: walletActive ? 0 : 1,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <span
            key={playKey}
            className="block"
            style={{
              width: 54,
              height: 54,
              borderRadius: 27,
              animation: 'lens-pop 480ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              // Lighter pane than the bar, lit by a soft accent bloom. No
              // backdrop-filter — it already sits on the bar's frosted pane.
              background: 'rgba(255,255,255,0.55)',
              // Inset tint only — glassGlow's outer halo also pooled under the bar.
              boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.65), inset 0 0 14px 0 rgba(${GLASS_ACCENT},0.2)`,
            }}
          />
        </span>

        {/* Side tabs overlay — above capsule so icons aren't covered by the fill. */}
        <div
          className="absolute left-0 right-0 z-10 flex items-center justify-between"
          style={{ top: 6, height: 68 }}
        >
          <div className="flex items-center">
            <SideTab
              tab="home"
              anim={lottie.home}
              active={active === 'home'}
              playKey={playKey}
              onClick={() => select('home')}
            />
            <SideTab
              tab="cards"
              anim={lottie.cards}
              active={active === 'cards'}
              playKey={playKey}
              onClick={() => select('cards')}
            />
          </div>

          {/* centre wallet is rendered outside the glass bar (below) so it
              can be larger and overflow above the bar */}

          <div className="flex items-center">
            <SideTab
              tab="stats"
              anim={lottie.chart}
              active={active === 'stats'}
              playKey={playKey}
              onClick={() => select('stats')}
            />
            <SideTab
              tab="profile"
              anim={lottie.user}
              active={active === 'profile'}
              playKey={playKey}
              onClick={() => select('profile')}
            />
          </div>
        </div>

        {/* Centre glass disc — a SIBLING of the bar (NOT nested in the button),
            so it shares the bar's exact structure that makes backdrop-filter work
            on Android. Nesting it inside the <button> killed the filter (the disc
            rendered see-through). The button on top is transparent: icon + tap. */}
        <div
          aria-hidden
          className="pointer-events-none absolute overflow-hidden rounded-full"
          style={{
            left: '50%',
            top: 0,
            marginLeft: -39,
            width: 78,
            height: 78,
            zIndex: 20,
          }}
        >
          <div
            className="h-full w-full"
            style={{
              ...glassSurface('thick'),
              ...(walletActive
                ? {
                    backgroundColor: `rgba(${GLASS_ACCENT},0.9)`,
                    // Nothing to frost behind an accent fill this dense — drop the
                    // filter so it costs no compositing layer.
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none',
                    // Inset + thin accent ring only (no outer glow under the bar).
                    boxShadow: [
                      'inset 0 1px 0 0 rgba(255,255,255,0.45)',
                      `inset 0 0 16px 0 rgba(${GLASS_ACCENT},0.26)`,
                      `0 0 0 1px rgba(${GLASS_ACCENT},0.18)`,
                    ].join(', '),
                  }
                : {
                    // Rim only — no outer glow under the bar.
                    boxShadow: [
                      'inset 0 0 0 1px rgba(255,255,255,0.55)',
                      'inset 0 1px 0 0 rgba(255,255,255,0.9)',
                      'inset 0 -1px 0 0 rgba(255,255,255,0.3)',
                    ].join(', '),
                  }),
            }}
          />
        </div>

        {/* Raised centre wallet — larger than the side tabs and overflowing
            above the bar (outside the glass so it isn't clipped). */}
        <button
          type="button"
          onClick={() => select('wallet')}
          aria-label="wallet"
          aria-current={walletActive ? 'page' : undefined}
          className="absolute z-30 flex h-[78px] w-[78px] items-center justify-center rounded-full transition-transform duration-300 active:scale-95"
          style={{
            left: '50%',
            top: 0,
            marginLeft: -39,
          }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at 32% 16%, rgba(255,255,255,0.35), transparent 34%)',
              mixBlendMode: 'screen',
              clipPath: 'circle(50%)',
              WebkitClipPath: 'circle(50%)',
            }}
          />
          <div
            className="relative z-10"
            style={{
              transform: walletActive ? 'scale(1.18)' : 'scale(1)',
              transition: 'transform 380ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <LottieIcon
              animationData={lottie.wallet}
              size={32}
              color={walletActive ? WHITE : BLUE}
              playToken={walletActive ? playKey : 0}
            />
          </div>
        </button>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}
